import Script from 'next/script'
import React, { useState, useEffect, useRef, useMemo } from 'react'
import Button from '#/components/Button'
import OverlaySpinner from '#/components/OverlaySpinner'
import TextInput from '#/components/TextInput'
import { logEvent } from '#/services/analytics'
import { SubmitLead } from '#/store/actions/property'
import { useInputState } from '#/utils/hook'
import { showSuccess } from '#/utils/message'

type LeadProps = {
  asyncStatus: TopHap.GlobalState['status']
  property: TopHap.Property
  submitLead: typeof SubmitLead
  leadOrigin: 'map-detail' | 'detail'
  onClose?: () => void
}

const LeadForm: React.FC<LeadProps> = ({
  asyncStatus,
  property,
  leadOrigin,
  submitLead,
  onClose
}) => {
  const [name, setName] = useInputState('')
  const [phone, setPhone] = useInputState('')
  const [email, setEmail] = useInputState('')
  const [loading, setLoading] = useState(true)
  const scriptUrl = useMemo(() => (document.location.protocol === 'https:' ? 'https' : 'http') + '://api.trustedform.com/trustedform.js?field=TrustedFormCertUrl&ping_field=xxTrustedFormPingUrl&l=' + new Date().getTime() + Math.random() + (process.env.NODE_ENV === 'production' ? '' : '&sandbox=true'), [])

  const isWaiting = useRef(false)

  useEffect(() => {
    if (asyncStatus[SubmitLead.type] === 'success' && isWaiting.current) {
      (async () => {
        await showSuccess({
          title: 'Success',
          content: 'A local agent will reach out shortly'
        })
      })()

      isWaiting.current = false

      if (onClose) onClose()
    }
  }, [asyncStatus[SubmitLead.type]])

  const handleSubmit = (ev: any) => {
    ev.preventDefault()

    const payload: Record<string, string> = {}

    for (let i = 0; i < ev.target.length; ++i) {
      payload[ev.target[i].name] = ev.target[i].value
    }

    const request: TopHap.Service.SubmitLeadRequest = {
      trusted_form_cert_url: payload.TrustedFormCertUrl,
      lead_origin: leadOrigin,
      referer: location.href,
      name,
      phone,
      property_id: property.id
    }

    submitLead(request)

    logEvent('generate_lead', {
      content_type: 'request_info',
      value: property.id,
      address: property.address.FullAddress,
      listhub_key: property.isListHub ? property.ListingKey : undefined,
      name,
      phone,
      email
    })

    isWaiting.current = true
  }

  const setCleanPhone = (e: any) => {
    const cleaned = ('' + e.target.value).replace(/\D/g, '')
    if (cleaned) {
      if (cleaned.length < 4) {
        setPhone(cleaned)
      } else if (cleaned.length < 7) {
        setPhone(`(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`)
      } else {
        setPhone(`(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`)
      }
    } else {
      setPhone('')
    }
  }

  const logPhoneEvent = () => {
    logEvent('generate_lead', {
      content_type: 'phone',
      value: property?.id,
      address: property?.address.FullAddress,
      listhub_key: property?.isListHub ? property.ListingKey : undefined
    })
  }

  return (
    <>
      <form
        className='relative grid'
        method='POST'
        onSubmit={handleSubmit}
      >

        <h2>Connect with a Local Agent in your Area</h2>
        <span className='mb-1 mt-3 border-b border-border pb-2 text-sm' itemProp='telephone'>
          Call us now at <a className='text-primary' href='tel:+1855-630-0577' onClick={logPhoneEvent}>855-630-0577</a> or fill in the contact form
        </span>
        <div>
          <TextInput
            className='my-1 w-full'
            icon='person'
            label='Name'
            name='name'
            placeholder='Name'
            required
            value={name}
            onChange={setName}
          />
          <TextInput
            className='my-1 w-full'
            icon='phone'
            label='Phone'
            name='phone'
            placeholder='Phone'
            required
            type='phone'
            value={phone}
            onChange={setCleanPhone}
          />
          <TextInput
            className='my-1 w-full'
            icon='mail'
            label='Email'
            name='email'
            placeholder='Email'
            required
            type='email'
            value={email}
            onChange={setEmail}
          />

          {/* <input id='xxTrustedFormCertUrl_0' name='xxTrustedFormCertUrl' type='hidden' value='https://cert.trustedform.com/454a35b802f3e7b63ffabb4efedb7c6ebe67886c' />
          <input id='xxTrustedFormPingUrl_0' name='xxTrustedFormPingUrl' type='hidden' value='https://ping.trustedform.com/0.HJDqajg8vVF20hwiDBJ92kct5idepgBM4vHylmh43kShfWSZKwm8HI37KqPToi0y3dhTIQ3N.uOjUbzs7aKNUWivkGh7I9Q.50hgmcLFsDabNx1wtZZPdQ' /> */}
        </div>
        <span className='mt-2 text-xs text-slate-400'>
          CONSENT. By proceeding, you consent to receive calls and texts at the number you
          provided, including marketing by autodialer and prerecorded and artificial voice, and
          email, from Realtor.com (Move Sales, Inc.), its affiliates, and their
          network service providers, about your inquiry and other home-related matters, but not
          as a condition of any purchase. Msg/data rates may apply. This consent applies even if
          you are on a corporate, state, or national Do Not Call list.
        </span>
        <div className='mt-3 flex w-full justify-end'>
          <Button className='my-2 h-8 rounded-lg bg-primary px-10 py-5 text-xs font-medium text-white' type='submit'>
            Submit
          </Button>
        </div>

        <OverlaySpinner
          absolute
          visible={loading || asyncStatus[SubmitLead.type] === 'request'}
        />
      </form>

      {/* TrustedForm */}
      <Script
        id='trusted_form'
        src={scriptUrl}
        onLoad={() => setLoading(true)}
        onReady={() => setLoading(false)}
      />
      <noscript>
        {/* eslint-disable-next-line jsx-a11y/alt-text */}
        <img src='https://api.trustedform.com/ns.gif' />
      </noscript>
    </>
  )
}

export default LeadForm
